'use client';

import { useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import {
  type PriorAuthSummaryT,
  usePatientPriorAuthsQuery,
  usePriorAuthSyncMutation,
} from '@/entities/prior-auth';
import { openPriorAuthActionOverlay, type PriorAuthActionMode } from '@/features/prior-auth-form';
import { openPaCompletedRxViewOverlay } from '@/features/pa-completed-rx-view';
import { WorkspaceSection } from '@/shared/ui/primitives';
import { Button } from '@/shared/ui/shadcn/button';

import { EligibilitiesSection } from './EligibilitiesSection';
import { PriorAuthsTable } from './PriorAuthsTable';

interface PatientPriorAuthsTabProps {
  userId: string;
}

/**
 * Statuses we consider "completed" — clicking one of these rows opens the
 * read-only Rx view with the authorization number pre-filled rather than
 * the action wizard.
 *
 * Mirrors ``CARE_STATE_PRIOR_AUTH_TERMINAL_STATUSES`` on the backend.
 */
const TERMINAL_STATUSES = new Set<string>([
  'Approved',
  'Partially Approved',
  'Not Required',
  'Denied',
  'Cancelled',
  'Error',
  'Sent to Pharmacy',
]);

const ACTION_REQUIRED_STATUS = 'Action Required';

function defaultModeForSummary(summary: PriorAuthSummaryT): PriorAuthActionMode {
  if (summary.attachmentRequired) return 'upload_attachment';
  if (summary.status === ACTION_REQUIRED_STATUS) return 'answer_question';
  if (summary.canAppeal === true) return 'appeal';
  return 'answer_question';
}

/**
 * Tab renderer for the patient workspace.
 *
 * Clicking a row routes to one of two overlays depending on the case state:
 *
 * - Terminal PAs (``TERMINAL_STATUSES``): the read-only Rx summary with the
 *   authorization number pre-filled, so the clinician can confirm the
 *   approved Rx matches what was written.
 * - Everything else: the :class:`PriorAuthActionWizard`, pre-seeded with
 *   the mode that's most likely to move the case forward.
 */
export function PatientPriorAuthsTab({ userId }: PatientPriorAuthsTabProps) {
  const query = usePatientPriorAuthsQuery(userId);
  const syncMutation = usePriorAuthSyncMutation();

  const handleSelect = useCallback(
    (summary: PriorAuthSummaryT) => {
      if (TERMINAL_STATUSES.has(summary.status)) {
        openPaCompletedRxViewOverlay({
          patientUserId: userId,
          priorAuthRecordId: summary.id,
          prescriptionRecordId: summary.prescriptionRecordId,
          dosespotPrescriptionId: summary.dosespotPrescriptionId,
          authorizationNumber: summary.authorizationNumber ?? null,
          status: summary.status,
        });
        return;
      }

      const caseId = Number(summary.dosespotPriorAuthCaseNumber ?? 0);
      openPriorAuthActionOverlay({
        mode: defaultModeForSummary(summary),
        recordId: summary.id,
        caseId,
        patientUserId: userId,
        dosespotPrescriptionId: summary.dosespotPrescriptionId,
      });
    },
    [userId]
  );

  const handleSync = useCallback(() => {
    syncMutation.mutate(
      { patientUserId: userId },
      {
        onSuccess: (result) => {
          // The result is intentionally terse — the PA pipeline has two
          // stages (poll → process) and the toast summarizes both so the
          // clinician can tell whether CareState actually changed.
          const bits: string[] = [];
          bits.push(
            `${result.polled} polled` +
              (result.transitions ? ` (${result.transitions} changed)` : '')
          );
          if (result.discovered) {
            bits.push(`${result.discovered} discovered`);
          }
          if (result.requestsOpened || result.requestsResolved) {
            const parts: string[] = [];
            if (result.requestsOpened) parts.push(`${result.requestsOpened} opened`);
            if (result.requestsResolved) parts.push(`${result.requestsResolved} resolved`);
            bits.push(parts.join(', '));
          }
          toast.success(`Synced with DoseSpot — ${bits.join(' · ')}`);
        },
        onError: () => {
          toast.error('DoseSpot sync failed.');
        },
      }
    );
  }, [syncMutation, userId]);

  const isSyncing = syncMutation.isPending;

  return (
    <div className="space-y-6" aria-label="Prior authorizations">
      <WorkspaceSection
        title="Prior Authorizations"
        description="Outstanding and completed PA cases for this patient. Click a row to act on the case."
        action={
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleSync}
            disabled={isSyncing}
            aria-label="Sync prior authorizations with DoseSpot"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isSyncing ? 'animate-spin' : ''}`} aria-hidden />
            {isSyncing ? 'Syncing…' : 'Sync with DoseSpot'}
          </Button>
        }
      >
        {query.isFetching && !query.isLoading ? (
          <p className="mb-2 text-[10px] text-muted-foreground">Refreshing…</p>
        ) : null}
        <PriorAuthsTable userId={userId} onSelect={handleSelect} />
      </WorkspaceSection>

      <EligibilitiesSection userId={userId} />
    </div>
  );
}
